import { Link, type To } from "react-router";
import { ChevronRight, Home } from "lucide-react";
import { cn } from "@/shared/utils/style.util";

type BreadcrumbItem = {
  label: string
  to?: To
  path?: To
};

type BreadcrumbProps = {
  items: BreadcrumbItem[]
  className?: string
};

export const Breadcrumb = ({ items, className = "" }: BreadcrumbProps) => {
  // Toujours ajouter "Accueil" comme premier élément
  const homeItem: BreadcrumbItem = { label: "Accueil", to: "/" };
  const allItems: BreadcrumbItem[] = [homeItem, ...items];

  if (allItems.length === 1 && allItems[0].label === "Accueil" && allItems[0].to === "/") {
    // Si on a seulement "Accueil", ne pas afficher le breadcrumb
    return null;
  }

  return (
    <nav className={cn(className, "mb-4")} aria-label="Breadcrumb">
      <ol className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600 dark:text-gray-400 sm:space-x-2 sm:gap-y-0 sm:text-sm">
        {allItems.map((item, index) => {
          const isLastItem = index === allItems.length - 1;
          const to = item.to ?? item.path;
          return (
            <li
              key={item.label}
              className={`flex items-center ${isLastItem ? "min-w-0 flex-1" : "flex-shrink-0"}`}
            >
              {index > 0 && (
                <ChevronRight className="mr-1.5 h-3.5 w-3.5 shrink-0 text-gray-400 sm:mr-2 sm:h-4 sm:w-4" aria-hidden />
              )}
              {to
                ? (
                  <Link
                    to={to}
                    className={`flex min-w-0 items-center gap-1.5 text-gray-600 hover:text-primary dark:text-darklink dark:hover:text-primary transition-colors duration-200 sm:gap-2 ${isLastItem ? "min-w-0" : ""}`}
                  >
                    {index === 0 && <Home className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />}
                    <span className={isLastItem ? "truncate sm:max-w-none" : "whitespace-nowrap"}>{item.label}</span>
                  </Link>
                )
                : (
                  <span className={`flex items-center gap-1.5 text-gray-700 dark:text-white font-medium sm:gap-2 ${isLastItem ? "min-w-0" : ""}`}>
                    {index === 0 && <Home className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />}
                    <span className={isLastItem ? "truncate sm:max-w-none" : "whitespace-nowrap"}>{item.label}</span>
                  </span>
                )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export type { BreadcrumbItem };
